import { useTranslation } from 'react-i18next'
import { Users, X } from '@digital-twins/design-system/icons'

import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import {
  QUICK_STUDY_MIN_PANEL,
  SegmentChip,
  SegmentGateNotice,
} from './runSegmentFilter'

/**
 * "Who answers" on the review panel: one removable chip per condition of the
 * run's segment, the live match count beside them, and the one-line notice
 * when the segment is too small to test (blocks approval) or too small to
 * break down well (warns).
 *
 * Chips are inert labels with a trailing dismiss, the same shape as the
 * composer's command chips. Nothing renders for a full-audience run.
 */
interface RunGateSegmentChipsProps {
  chips: Array<SegmentChip>
  matchingCount: number | null
  isCounting: boolean
  /** Owner, gate pending and not mid-update. */
  canEdit: boolean
  notice: SegmentGateNotice | null
  /** Shown when the last update was refused. */
  errorMessage?: string | null
  onRemove: (index: number) => void
  onClearAll: () => void
}

export function RunGateSegmentChips(props: RunGateSegmentChipsProps) {
  const {
    chips,
    matchingCount,
    isCounting,
    canEdit,
    notice,
    errorMessage = null,
    onRemove,
    onClearAll,
  } = props

  const { t } = useTranslation('app')

  if (chips.length === 0) {
    return null
  }

  const countLabel = isCounting
    ? t('threads.detail.gate.segment.counting')
    : matchingCount !== null
      ? t('threads.detail.gate.segment.matching', { count: matchingCount })
      : null

  const isBlocking = notice?.kind === 'below_minimum' || notice?.kind === 'count_failed'
  const noticeTestId =
    notice?.kind === 'below_minimum'
      ? 'run-gate-segment-below-minimum'
      : notice?.kind === 'count_failed'
        ? 'run-gate-segment-count-failed'
        : 'run-gate-segment-thin'
  const noticeText =
    notice?.kind === 'below_minimum'
      ? t('threads.detail.gate.segment.belowMinimum', {
          count: notice.count,
          min: QUICK_STUDY_MIN_PANEL,
        })
      : notice?.kind === 'count_failed'
        ? t('threads.detail.gate.segment.countFailed')
        : notice?.kind === 'thin_breakdowns'
          ? t('threads.detail.gate.segment.thinBreakdowns', { count: notice.count })
          : null

  const noticeLine = noticeText ? (
    <Text
      size="xs"
      className={cn(
        'px-1',
        isBlocking ? 'text-destructive' : 'text-amber-600 dark:text-amber-400',
      )}
      data-testid={noticeTestId}
    >
      {noticeText}
    </Text>
  ) : null

  return (
    <div className="space-y-1.5 px-1 pb-2" data-testid="run-gate-segment">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
          data-testid="run-gate-segment-label"
        >
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          {t('threads.detail.gate.segment.label')}
        </span>
        {chips.map((chip) => (
          <span
            key={`${chip.index}-${chip.label}`}
            className="inline-flex h-6 max-w-[220px] items-center gap-1 rounded-full bg-[var(--home-pill-track-bg)] py-0 pl-2 pr-0.5 text-[12px] font-medium leading-none text-[var(--home-composer-fg)]"
            data-testid={`run-gate-segment-chip-${chip.index}`}
          >
            <span className="truncate">{chip.label}</span>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => onRemove(chip.index)}
              aria-label={t('threads.detail.gate.segment.remove', { label: chip.label })}
              className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full opacity-55 transition-opacity duration-200 hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30"
              data-testid={`run-gate-segment-chip-${chip.index}-remove`}
            >
              <X className="h-3 w-3" strokeWidth={2.4} />
            </button>
          </span>
        ))}
        {countLabel ? (
          <Text
            as="span"
            size="xs"
            color="muted"
            className="leading-none"
            data-testid="run-gate-segment-count"
          >
            {countLabel}
          </Text>
        ) : null}
        {chips.length > 1 ? (
          <button
            type="button"
            disabled={!canEdit}
            onClick={onClearAll}
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="run-gate-segment-clear"
          >
            {t('threads.detail.gate.segment.clearAll')}
          </button>
        ) : null}
      </div>
      {noticeLine}
      {errorMessage ? (
        <Text
          size="xs"
          className="px-1 text-destructive"
          data-testid="run-gate-segment-error"
        >
          {errorMessage}
        </Text>
      ) : null}
    </div>
  )
}
